import React, { useState, useEffect } from 'react';
import { useInventory } from '../hooks/useInventory';
import { supabase } from '../lib/supabase';
import { DynamicIcon } from '../components/icons/DynamicIcon';
import { InventoryAlert } from '../components/InventoryAlert';
import toast from 'react-hot-toast';

interface Ingredient {
  id: string;
  name: string;
  unit: string;
  current_stock: number;
  minimum_stock: number;
  preco_standard: number;
  preco_premium: number;
}

const InventoryManagement: React.FC = () => {
  const { summary, loading, error, addInventoryTransaction } = useInventory();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loadingIngredients, setLoadingIngredients] = useState(true);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [transactionType, setTransactionType] = useState<'purchase' | 'usage' | 'adjustment' | 'waste'>('purchase');
  const [quantity, setQuantity] = useState<number>(0);
  const [unitPrice, setUnitPrice] = useState<number | undefined>(undefined);
  const [notes, setNotes] = useState<string>('');
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      setLoadingIngredients(true);
      const { data, error } = await supabase
        .from('ingredients')
        .select('*')
        .order('name');

      if (error) throw error;
      setIngredients(data || []);
    } catch (err: any) {
      console.error('Error fetching ingredients:', err);
      toast.error('Erro ao carregar ingredientes');
    } finally {
      setLoadingIngredients(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!selectedIngredient) {
      toast.error('Selecione um ingrediente');
      return;
    }

    if (quantity <= 0) {
      toast.error('A quantidade deve ser maior que zero');
      return;
    }

    if (transactionType === 'purchase' && !unitPrice) {
      toast.error('Informe o preço unitário para compras');
      return;
    }

    const result = await addInventoryTransaction(
      selectedIngredient.id,
      transactionType,
      quantity,
      unitPrice,
      notes
    );

    if (!result.error) {
      setShowTransactionModal(false);
      setSelectedIngredient(null);
      setQuantity(0);
      setUnitPrice(undefined);
      setNotes('');
      fetchIngredients(); // Refresh ingredients list
    }
  };

  const openTransactionModal = (ingredient: Ingredient, type: 'purchase' | 'usage' | 'adjustment' | 'waste') => {
    setSelectedIngredient(ingredient);
    setTransactionType(type);
    
    // Set default values based on transaction type
    if (type === 'purchase') {
      setQuantity(ingredient.minimum_stock);
      setUnitPrice(ingredient.preco_standard);
    } else if (type === 'usage') {
      setQuantity(1);
      setUnitPrice(undefined);
    } else if (type === 'adjustment') {
      setQuantity(ingredient.current_stock);
      setUnitPrice(undefined);
    } else {
      setQuantity(0);
      setUnitPrice(undefined);
    }
    
    setShowTransactionModal(true);
  };

  const getStockStatusColor = (current: number, minimum: number) => {
    const ratio = current / minimum;
    if (ratio <= 0.25) return 'text-red-600';
    if (ratio <= 0.5) return 'text-amber-600';
    if (ratio <= 0.75) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const filteredIngredients = ingredients.filter(ingredient => 
    ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && loadingIngredients) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Carregando dados de inventário...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <DynamicIcon name="alertCircle" className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Erro ao carregar dados de inventário: {error}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <DynamicIcon name="package" size={32} className="text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Gestão de Estoque</h1>
            </div>
            <InventoryAlert />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <DynamicIcon name="package" size={32} className="text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total de Ingredientes</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {summary.totalItems}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <DynamicIcon name="alertTriangle" size={32} className="text-amber-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Estoque Baixo</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {summary.lowStockCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <DynamicIcon name="dollarSign" size={32} className="text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Valor Total em Estoque</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(summary.totalValue)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DynamicIcon name="search" size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar ingredientes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchIngredients()}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <DynamicIcon name="refreshCw" size={16} className="mr-2" />
                    Atualizar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Ingredients Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Ingredientes em Estoque</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ingrediente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estoque Atual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estoque Mínimo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Unitário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor em Estoque
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredIngredients.map((ingredient) => (
                    <tr key={ingredient.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{ingredient.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`${getStockStatusColor(ingredient.current_stock, ingredient.minimum_stock)} font-medium`}>
                          {ingredient.current_stock} {ingredient.unit}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-500">
                          {ingredient.minimum_stock} {ingredient.unit}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-500">
                          {formatCurrency(ingredient.preco_standard)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-500">
                          {formatCurrency(ingredient.current_stock * ingredient.preco_standard)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openTransactionModal(ingredient, 'purchase')}
                            className="text-blue-600 hover:text-blue-900"
                            title="Registrar Compra"
                          >
                            <DynamicIcon name="plus" size={16} />
                          </button>
                          <button
                            onClick={() => openTransactionModal(ingredient, 'usage')}
                            className="text-amber-600 hover:text-amber-900"
                            title="Registrar Uso"
                          >
                            <DynamicIcon name="minus" size={16} />
                          </button>
                          <button
                            onClick={() => openTransactionModal(ingredient, 'adjustment')}
                            className="text-green-600 hover:text-green-900"
                            title="Ajustar Estoque"
                          >
                            <DynamicIcon name="edit" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Modal */}
      {showTransactionModal && selectedIngredient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <div className="flex items-center">
                {transactionType === 'purchase' && <DynamicIcon name="plus" size={24} className="text-blue-600 mr-3" />}
                {transactionType === 'usage' && <DynamicIcon name="minus" size={24} className="text-amber-600 mr-3" />}
                {transactionType === 'adjustment' && <DynamicIcon name="edit" size={24} className="text-green-600 mr-3" />}
                {transactionType === 'waste' && <DynamicIcon name="trash" size={24} className="text-red-600 mr-3" />}
                <h2 className="text-xl font-semibold text-gray-900">
                  {transactionType === 'purchase' && 'Registrar Compra'}
                  {transactionType === 'usage' && 'Registrar Uso'}
                  {transactionType === 'adjustment' && 'Ajustar Estoque'}
                  {transactionType === 'waste' && 'Registrar Desperdício'}
                </h2>
              </div>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <DynamicIcon name="x" size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ingrediente
                </label>
                <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                  {selectedIngredient.name}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Transação
                </label>
                <select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value as any)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="purchase">Compra (Entrada)</option>
                  <option value="usage">Uso (Saída)</option>
                  <option value="adjustment">Ajuste de Estoque</option>
                  <option value="waste">Desperdício (Saída)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade ({selectedIngredient.unit})
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quantity}
                  onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {transactionType === 'adjustment' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Estoque atual: {selectedIngredient.current_stock} {selectedIngredient.unit}
                  </p>
                )}
              </div>

              {transactionType === 'purchase' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço Unitário (R$)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitPrice || ''}
                    onChange={(e) => setUnitPrice(parseFloat(e.target.value) || undefined)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Opcional"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddTransaction}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;